/**
 * WorkflowPhasePipeline — unified vertical phase progress visualizer.
 *
 * Lifted from apps/outlook-addin/src/components/PhasePipeline.tsx (plugin)
 * and app/features/workflows/components/WorkflowPhasePipeline.tsx (ora).
 *
 * Both prior implementations are now re-export wrappers pointing here.
 *
 * API:
 *   phases            — ordered phase manifest from the catalog
 *   phaseStatuses     — computed statuses (PhaseStatus[] from phaseStatus.ts)
 *   renderHitlPanel   — optional inline panel slot for hitl_waiting nodes
 *   frozen            — suppresses running pulse (terminal mode)
 *   showAll           — reveals all nodes immediately (skip frontier animation)
 *   domainTypeColors  — optional type→badge map for domain-colour badges (ora uses this)
 *
 * Import alias in ora:     ~/common/workflows/components/WorkflowPhasePipeline
 * Import alias in add-in:  @product-app/common/workflows/components/WorkflowPhasePipeline
 */
import React, { useEffect, useRef } from "react";
import CustomTooltip from "~/common/components/CustomTooltip";
import type { PhaseDefinition, PhaseStatus } from "../types";
import { classifyHitlResponse, summarizeHitlResponse } from "../hitlSummary";
import { formatDecisionLatency } from "../decisionLatency";

export type { PhaseDefinition, PhaseStatus };

export interface DomainTypeColor {
  bgClass: string;
  textClass: string;
  label: string;
}

/** Lightweight HITL event shape consumed by the pipeline badge — a subset of
 * the full HitlEvent interface so this component does not need to depend on
 * the history-section component. */
export interface PipelineHitlEvent {
  sequence: number;
  step_id: string;
  event_type: string;
  response: Record<string, unknown> | null;
  asked_at: string;
  answered_at: string | null;
}

export interface WorkflowPhasePipelineProps {
  phases: PhaseDefinition[];
  phaseStatuses: PhaseStatus[];
  /** Optional inline panel rendered under the hitl_waiting phase node. */
  renderHitlPanel?: (phaseId: string) => React.ReactNode;
  /** When true the pipeline is frozen — no running pulse. */
  frozen?: boolean;
  /** When true all phases reveal immediately (no frontier animation). */
  showAll?: boolean;
  /**
   * Optional type→badge colour map. Pass `DOMAIN_TYPE_COLORS` from
   * `app/features/workflows/builder/domainTypeColors` in ora to render
   * per-phase domain badges (UI, Data, AI, Human, Control).
   * When absent no domain badges are shown.
   */
  domainTypeColors?: Record<string, DomainTypeColor>;
  /**
   * Optional map of step_id → past HITL event so the pipeline can overlay
   * a small ✓ / ✗ / ✏ / ⏱ badge on phases the AM has already decided.
   * Click the badge to scroll the parent's history section to the matching
   * card (parent provides the handler).
   */
  hitlEventsByStepId?: Record<string, PipelineHitlEvent>;
  /** Click-handler for the HITL badge — scrolls the parent's history list. */
  onHitlBadgeClick?: (sequence: number) => void;
  /**
   * Optional slot. When provided, the per-step description is wrapped with
   * whatever the parent returns — used in ora to attach a `CustomTooltip`
   * showing Start / End / Duration / Step ID / Kind / Position / etc.
   * The shared component stays library-agnostic by delegating tooltip
   * presentation to the parent; both apps can use `buildTooltipRows` from
   * `~/common/workflows/stepTooltipParts` to format identical content.
   */
  renderStepTooltipTrigger?: (
    children: React.ReactNode,
    info: import("../stepTooltipParts").StepTooltipInfo,
  ) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// State → visual mapping
// ---------------------------------------------------------------------------

export const STATE_TONE: Record<
  PhaseStatus["state"],
  { dot: string; label: string; text: string; pillBg: string; pillText: string }
> = {
  pending: {
    dot: "bg-primary-gray-light-300",
    label: "Pending",
    text: "text-primary-gray-light-400",
    pillBg: "bg-primary-gray-light-100",
    pillText: "text-primary-gray-light-500",
  },
  running: {
    dot: "bg-sunrise",
    label: "Running",
    text: "text-primary-gray-light-700",
    pillBg: "bg-sunrise/20",
    pillText: "text-primary-warning-700",
  },
  completed: {
    dot: "bg-primary-success-600",
    label: "Done",
    text: "text-primary-gray-light-700",
    pillBg: "bg-primary-success-50",
    pillText: "text-primary-success-700",
  },
  failed: {
    dot: "bg-primary-error-500",
    label: "Failed",
    text: "text-primary-gray-light-700",
    pillBg: "bg-primary-error-50",
    pillText: "text-primary-error-700",
  },
  hitl_waiting: {
    dot: "bg-primary-warning-500",
    label: "Waiting",
    text: "text-primary-gray-light-700",
    pillBg: "bg-primary-warning-50",
    pillText: "text-primary-warning-700",
  },
  skipped: {
    dot: "bg-primary-gray-light-400",
    label: "Skipped",
    text: "text-primary-gray-light-500",
    pillBg: "bg-primary-gray-light-100",
    pillText: "text-primary-gray-light-500",
  },
};

// ---------------------------------------------------------------------------
// PhaseNode
// ---------------------------------------------------------------------------

interface NodeProps {
  phase: PhaseDefinition;
  state: PhaseStatus["state"];
  errorMessage?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  index: number;
  total: number;
  isLast: boolean;
  revealed: boolean;
  isNextUp: boolean;
  frozen: boolean;
  domainColor?: DomainTypeColor | null;
  hitlEvent?: PipelineHitlEvent | null;
  onHitlBadgeClick?: (sequence: number) => void;
  renderStepTooltipTrigger?: (
    children: React.ReactNode,
    info: import("../stepTooltipParts").StepTooltipInfo,
  ) => React.ReactNode;
  children?: React.ReactNode;
}

const HITL_BADGE_GLYPH: Record<string, string> = {
  approve: "✓",
  reject: "✗",
  corrections: "✏",
  timeout: "⏱",
  unknown: "•",
};

const HITL_BADGE_CLASSES: Record<string, string> = {
  approve: "bg-primary-success-50 text-primary-success-700 border-primary-success-200",
  reject: "bg-primary-error-50 text-primary-error-700 border-primary-error-200",
  corrections: "bg-primary-warning-50 text-primary-warning-700 border-primary-warning-200",
  timeout: "bg-primary-gray-light-100 text-primary-gray-light-600 border-primary-gray-light-300",
  unknown: "bg-primary-gray-light-50 text-primary-gray-light-500 border-primary-gray-light-200",
};

function HitlBadge({
  event,
  onClick,
}: {
  event: PipelineHitlEvent;
  onClick?: (sequence: number) => void;
}) {
  const kind = classifyHitlResponse(event.response, event.event_type);
  const summary = summarizeHitlResponse(event.response, event.event_type);
  const latency = formatDecisionLatency(event.asked_at, event.answered_at);
  const tooltip = latency ? `${summary} · ${latency}` : summary;
  const glyph = HITL_BADGE_GLYPH[kind] ?? HITL_BADGE_GLYPH.unknown;
  const classes = HITL_BADGE_CLASSES[kind] ?? HITL_BADGE_CLASSES.unknown;
  // ora's tooltip, not a native title (story 28633); the badge keeps its own
  // aria-label as its name.
  return (
    <CustomTooltip
      id={`hitl-badge-${event.step_id}`}
      title={tooltip}
      placement="top"
      arrow
      isOnModal
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(event.sequence);
        }}
        aria-label={`HITL #${event.sequence}: ${tooltip}`}
        data-testid={`hitl-badge-${event.step_id}`}
        className={`text-2xs font-semibold w-5 h-5 inline-flex items-center justify-center rounded-full border ${classes} hover:opacity-80 cursor-pointer`}
      >
        {glyph}
      </button>
    </CustomTooltip>
  );
}

const PhaseNode: React.FC<NodeProps> = ({
  phase,
  state,
  errorMessage,
  startedAt,
  endedAt,
  index,
  total,
  isLast,
  revealed,
  isNextUp,
  frozen,
  domainColor,
  hitlEvent,
  onHitlBadgeClick,
  renderStepTooltipTrigger,
  children,
}) => {
  const tone = STATE_TONE[state];
  const showPill =
    state === "running" ||
    state === "hitl_waiting" ||
    state === "failed" ||
    state === "skipped";

  const dotClasses = [
    "h-[10px] w-[10px] rounded-full flex-shrink-0",
    "transition-colors duration-300",
    tone.dot,
    state === "running" && !frozen ? "motion-safe:animate-pulse" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const connectorClasses = [
    "w-[2px] flex-1 my-1",
    "transition-colors duration-300",
    state === "completed"
      ? "bg-primary-success-600"
      : state === "failed"
        ? "bg-primary-error-300"
        : state === "skipped"
          ? "bg-primary-gray-light-400"
          : "bg-primary-gray-light-200",
  ].join(" ");

  return (
    <div
      data-testid={`phase-node-${state}`}
      data-phase-state={state}
      className={[
        "overflow-hidden",
        "transition-all duration-300 ease-out",
        revealed ? "opacity-100" : "opacity-0",
        revealed && isNextUp && state === "pending" ? "opacity-50" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ maxHeight: revealed ? 1200 : 0 }}
    >
      <div className="flex flex-row items-stretch gap-2.5">
        <div className="flex flex-col items-center w-4 flex-shrink-0 pt-1">
          <div className={dotClasses} />
          {!isLast && <div className={connectorClasses} />}
        </div>

        <div className="flex-1 min-w-0 pb-3">
          <div className="flex flex-row items-center gap-2 flex-wrap">
            {(() => {
              const labelEl = (
                <span
                  className={[
                    "text-xs leading-tight",
                    tone.text,
                    state === "running" || state === "hitl_waiting"
                      ? "font-semibold"
                      : "font-regular",
                  ].join(" ")}
                >
                  {phase.description || phase.id.replace(/_/g, " ")}
                </span>
              );
              // The slot decides whether to wrap with a tooltip and whether
              // to apply a help cursor — keeps the description plain when
              // there's no tooltip to show (e.g. pre-migration instances
              // with no phase_history rows).
              if (!renderStepTooltipTrigger) return labelEl;
              return renderStepTooltipTrigger(labelEl, {
                phase,
                state,
                startedAt,
                endedAt,
                errorMessage,
                index,
                total,
                hitlEvent: hitlEvent ?? null,
              });
            })()}
            {domainColor && (
              <span
                className={`text-[10px] font-semibold px-1.5 py-px rounded ${domainColor.bgClass} ${domainColor.textClass}`}
              >
                {domainColor.label}
              </span>
            )}
            {showPill && (
              <span
                className={[
                  "text-2xs font-semibold px-1.5 py-[1px] rounded",
                  "transition-colors duration-300",
                  tone.pillBg,
                  tone.pillText,
                ].join(" ")}
              >
                {tone.label}
              </span>
            )}
            {hitlEvent && (
              <HitlBadge event={hitlEvent} onClick={onHitlBadgeClick} />
            )}
          </div>

          {state === "failed" && errorMessage && (
            <div
              role="alert"
              className="mt-1 text-2xs text-primary-error-700 bg-primary-error-50 border border-primary-error-200 rounded px-2 py-1 break-words"
            >
              {errorMessage}
            </div>
          )}

          {children && <div className="mt-2 w-full">{children}</div>}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// WorkflowPhasePipeline
// ---------------------------------------------------------------------------

export function WorkflowPhasePipeline({
  phases,
  phaseStatuses,
  renderHitlPanel,
  frozen = false,
  showAll = false,
  domainTypeColors,
  hitlEventsByStepId,
  onHitlBadgeClick,
  renderStepTooltipTrigger,
}: WorkflowPhasePipelineProps) {
  const statusMap = new Map(phaseStatuses.map((s) => [s.phase_id, s.state]));
  const errorMap = new Map(
    phaseStatuses.map((s) => [s.phase_id, s.error_message]),
  );
  const startedMap = new Map(
    phaseStatuses.map((s) => [s.phase_id, s.started_at ?? null]),
  );
  const endedMap = new Map(
    phaseStatuses.map((s) => [s.phase_id, s.ended_at ?? null]),
  );

  const lastActiveIdx = phases.reduce(
    (max, p, i) =>
      (statusMap.get(p.id) ?? "pending") !== "pending" ? i : max,
    -1,
  );
  const frontier = lastActiveIdx + 1;

  const revealedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    phases.forEach((p, i) => {
      if (i <= frontier) revealedRef.current.add(p.id);
    });
  }, [phases, frontier]);

  if (phases.length === 0) {
    return (
      <div className="text-sm text-primary-gray-light-500 italic py-1">
        No step definitions available for this workflow.
      </div>
    );
  }

  return (
    <div
      className="flex flex-col py-1"
      role="list"
      aria-label="Workflow steps"
    >
      {phases.map((phase, i) => {
        const state = statusMap.get(phase.id) ?? "pending";
        const revealed = i <= frontier || frozen || showAll;
        const isNextUp = i === frontier;
        const domainColor = domainTypeColors?.[phase.type] ?? null;
        const hitlEvent = hitlEventsByStepId?.[phase.id] ?? null;
        return (
          <div role="listitem" key={phase.id}>
            <PhaseNode
              phase={phase}
              state={state}
              errorMessage={errorMap.get(phase.id) ?? null}
              startedAt={startedMap.get(phase.id) ?? null}
              endedAt={endedMap.get(phase.id) ?? null}
              index={i}
              total={phases.length}
              isLast={i === phases.length - 1}
              revealed={revealed}
              isNextUp={isNextUp}
              frozen={frozen}
              domainColor={domainColor}
              hitlEvent={hitlEvent}
              onHitlBadgeClick={onHitlBadgeClick}
              renderStepTooltipTrigger={renderStepTooltipTrigger}
            >
              {state === "hitl_waiting" && renderHitlPanel
                ? renderHitlPanel(phase.id)
                : null}
            </PhaseNode>
          </div>
        );
      })}
    </div>
  );
}

export default WorkflowPhasePipeline;

export { derivePhaseStatusesFromInstance } from "../phaseStatus";
